/**
 * Main Push Notification Manager
 * Provides a high-level interface for managing push notifications
 */

import { PushClient } from './pushClient.js';
import { APPLICATION_KEYS, DEFAULT_NOTIFICATION_OPTIONS } from './constants.js';

export class PushNotificationManager {
  constructor(options = {}) {
    this.options = {
      ...DEFAULT_NOTIFICATION_OPTIONS,
      ...options
    };

    this.currentSubscription = null;
    this.isSubscribed = false;
    this.pushEnabled = false;
    this.interactive = false;

    // Event callbacks
    this.onStateChange = options.onStateChange || (() => {});
    this.onSubscriptionChange = options.onSubscriptionChange || (() => {});
    this.onError = options.onError || (() => {});

    // Initialize push client
    this.pushClient = new PushClient(
      this.handleStateChange.bind(this),
      this.handleSubscriptionUpdate.bind(this),
      APPLICATION_KEYS.publicKey
    );

    // Set auth header provider if available in options
    if (options.getAuthHeader) {
      this.pushClient.setAuthHeaderProvider(options.getAuthHeader);
    }
  }

  handleStateChange(state, data) {
    // Update internal state
    this.pushEnabled = state.pushEnabled !== undefined ? state.pushEnabled : this.pushEnabled;
    this.interactive = state.interactive !== undefined ? state.interactive : this.interactive;

    // Handle specific states
    switch (state.id) {
      case 'UNSUPPORTED':
        this.onError('Push notifications not supported', data);
        break;
      case 'ERROR':
        this.onError('Push notification error', data);
        break;
      case 'SUBSCRIBED':
        this.isSubscribed = true;
        break;
      case 'UNSUBSCRIBED':
        this.isSubscribed = false;
        break;
      default:
        // Handle other states without specific actions
        break;
    }

    // Notify external listeners
    this.onStateChange(state, data);
  }

  handleSubscriptionUpdate(subscription) {
    this.currentSubscription = subscription;
    this.onSubscriptionChange(subscription);
  }

  // Public API methods

  /**
   * Check if push notifications are supported
   */
  static isSupported() {
    return PushClient.isSupported();
  }

  /**
   * Get current push notification permission
   */
  getPermission() {
    return Notification.permission;
  }

  /**
   * Check if currently subscribed to push notifications
   */
  isSubscribedToPush() {
    return this.isSubscribed && this.currentSubscription !== null;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    try {
      // The pushClient already handles sending subscription to backend
      const subscription = await this.pushClient.subscribeDevice();
      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    try {
      // The pushClient already handles removing subscription from backend
      await this.pushClient.unsubscribeDevice();
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      throw error;
    }
  }

  /**
   * Send a test push notification (for development/testing)
   */
  async sendTestNotification(message = 'Test notification from Sloot!') {
    if (!this.currentSubscription) {
      throw new Error('No active subscription');
    }

    try {
      // Send test notification via backend API
      const response = await fetch('/api/push/send', {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if available
          ...(this.pushClient.getAuthHeader && this.pushClient.getAuthHeader())
        },
        body: JSON.stringify({
          title: 'Test Notification',
          body: message,
          icon: '/sloot_logo.png',
          tag: 'test-notification',
          data: {
            test: true,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send test notification: ${response.status}`);
      }

      const result = await response.json();
      console.log('Test notification sent:', result);
      return result;
    } catch (error) {
      console.error('Failed to send test notification:', error);
      throw error;
    }
  }

  /**
   * Set authentication header provider
   */
  setAuthHeaderProvider(getAuthHeader) {
    this.pushClient.setAuthHeaderProvider(getAuthHeader);
  }

  /**
   * Get current subscription details
   */
  getSubscription() {
    return this.currentSubscription;
  }

  /**
   * Get subscription in JSON format for debugging
   */
  getSubscriptionJSON() {
    return this.currentSubscription ? JSON.stringify(this.currentSubscription, null, 2) : null;
  }

  /**
   * Show a local notification (not push)
   */
  async showLocalNotification(title, options = {}) {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
    }

    const notificationOptions = {
      ...this.options,
      ...options
    };

    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      return registration.showNotification(title, notificationOptions);
    } else {
      return new Notification(title, notificationOptions);
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
}
